import * as angular from 'angular';
import * as moment from 'moment';
import { TableColumn } from '@models/components/tableColumn';

type StatusOption = {
    value: string;
    label: string;
};

class ActivityDetailTableController implements angular.IOnInit, angular.IOnChanges {
    public fromDate: Date;
    public toDate: Date;
    public columns: TableColumn[];
    public statusOptions: StatusOption[];
    public fetchData: (args: { fromDate: Date; toDate: Date }) => angular.IPromise<any>;
    public mapData: (args: { results: any[]; ctrl: any }) => any[];
    public title: string;
    public icon: string;
    public storageKeyPrefix: string;
    public inline: boolean;

    public isLoading: boolean = false;
    public tableColumns: TableColumn[] = [];
    public allData: any[] = [];
    public filteredData: any[] = [];
    public searchTerm: string = '';
    public searchColumn: string = 'all';

    /** @ngInject */
    constructor(
        private notifyjs: any,
        private $timeout: angular.ITimeoutService
    ) {}

    $onInit(): void {
        const prefix = this.storageKeyPrefix || 'activityDetailTable';
        const savedSearchTerm = localStorage.getItem(prefix + 'SearchTerm');
        const savedSearchColumn = localStorage.getItem(prefix + 'SearchColumn');
        if (savedSearchTerm) {
            this.searchTerm = savedSearchTerm;
        }
        if (savedSearchColumn) {
            this.searchColumn = savedSearchColumn === 'Newly Opened' ? 'Opened' : savedSearchColumn;
        } else {
            this.searchColumn = 'all';
        }

        this.tableColumns = this.columns || [];
        this.filteredData = [];

        if (this.fromDate) {
            this.loadData();
        }
    }

    $onChanges(changes: angular.IOnChangesObject): void {
        if (
            (changes.fromDate && !changes.fromDate.isFirstChange()) ||
            (changes.toDate && !changes.toDate.isFirstChange())
        ) {
            if (this.fromDate) {
                this.loadData();
            }
        }
        if (changes.columns && this.columns) {
            this.tableColumns = this.columns;
        }
    }

    loadData(): void {
        if (!this.fromDate || !this.fetchData) {
            return;
        }

        this.isLoading = true;

        this.fetchData({ fromDate: this.fromDate, toDate: this.toDate })
            .then((response: any) => {
                const results = response && response.data && response.data.results;
                const rawResults = results && Array.isArray(results) ? results : [];

                if (this.mapData) {
                    this.allData = this.mapData({ results: rawResults, ctrl: this });
                } else {
                    this.allData = rawResults;
                }

                this.applySearchFilter();
            })
            .catch((err: any) => {
                this.notifyjs('Failed to load activity data: ' + (err.data || err.message || err));
                this.allData = [];
                this.filteredData = [];
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    getStatusHtml(displayStatus: string | null | undefined): string {
        if (!displayStatus) return "";

        let textClass = '';
        if (displayStatus === 'Reopened' || displayStatus === 'Newly Opened' || displayStatus === 'New') {
            textClass = 'text-success';
        } else if (displayStatus === 'Closed') {
            textClass = 'text-danger';
        }

        return textClass
            ? `<span class="${textClass}">${displayStatus}</span>`
            : `<span>${displayStatus}</span>`;
    }

    applySearchFilter(): void {
        let filtered = this.allData;

        if (this.searchColumn && this.searchColumn !== 'all') {
            const selectedStatus = this.searchColumn;
            filtered = filtered.filter((item: any) => {
                return item.statusValue === selectedStatus;
            });
        }

        if (this.searchTerm && this.searchTerm.trim() !== '') {
            const searchLower = this.searchTerm.toLowerCase().trim();
            filtered = filtered.filter((item: any) => {
                return this.tableColumns.some((column: TableColumn) => {
                    if (column.key === 'status') return false;
                    const value = item[column.key];
                    if (value === null || value === undefined) return false;
                    return String(value).toLowerCase().includes(searchLower);
                });
            });
        }

        this.filteredData = filtered.slice();
    }

    clearSearchTerm(): void {
        this.searchTerm = '';
        const prefix = this.storageKeyPrefix || 'activityDetailTable';
        localStorage.setItem(prefix + 'SearchTerm', this.searchTerm);
        this.applySearchFilter();
    }

    performSearch(): void {
        const prefix = this.storageKeyPrefix || 'activityDetailTable';
        localStorage.setItem(prefix + 'SearchTerm', this.searchTerm);
        localStorage.setItem(prefix + 'SearchColumn', this.searchColumn);
        this.$timeout(() => this.applySearchFilter(), 0);
    }

    onSearchColumnChange(): void {
        const prefix = this.storageKeyPrefix || 'activityDetailTable';
        localStorage.setItem(prefix + 'SearchColumn', this.searchColumn);
        this.$timeout(() => this.applySearchFilter(), 0);
    }

    getStatusOptions(): StatusOption[] {
        return this.statusOptions || [
            { value: 'Opened', label: 'Newly Opened' },
            { value: 'Closed', label: 'Closed' },
            { value: 'Reopened', label: 'Reopened' }
        ];
    }

    getTitle(): string {
        return this.title || 'Activity';
    }

    getIcon(): string {
        return this.icon || 'person_add';
    }
}

export const activityDetailTable: angular.IComponentOptions = {
    template: require('./activity-detail-table.html'),
    controller: ActivityDetailTableController,
    transclude: {
        'inlineStart': '?inlineStart',
        'inlineEnd': '?inlineEnd'
    },
    bindings: {
        fromDate: '<',
        toDate: '<',
        columns: '<',
        statusOptions: '<',
        fetchData: '<',
        mapData: '<',
        title: '@',
        icon: '@',
        storageKeyPrefix: '@',
        inline: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/fees/fees-components/activity-detail-table/activity-detail-table.ts